import { useCallback, useEffect, useRef, useState } from 'react';

import { Device } from '@twilio/voice-sdk';

import useTimer from '../useTimer';

import crmVoiceCallService from '../../domain/services/crm/crm.voice.call.service';

import { voiceCallStatuses } from '../../domain/constant/crm/crm.voice.call.status.constant';

const RECONNECT_TIMEOUT_MS = 20000;
const FIND_CALL_MAX_ATTEMPTS = 6;
const HIGH_RTT_THRESHOLD_MS = 220;

export const voiceCallRegisterStatuses = {
    idle: 'idle',
    searching: 'searching',
    found: 'found',
    notFound: 'notFound',
};

const useVoiceCallEngine = ({ contactId, opportunityId, customerId }) => {

    const [device, setDevice] = useState(null);
    const [tokenState, setTokenState] = useState({ token: '', info: {}, edge: '' });
    const [tokenError, setTokenError] = useState('');

    const { token, info: tokenInfo, edge: selectedEdge } = tokenState;

    const [callStatus, setCallStatus] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [callEndReason, setCallEndReason] = useState('');
    const [providerCallStatus, setProviderCallStatus] = useState('');
    const [isMuted, setIsMuted] = useState(false);

    const [microphoneStatus, setMicrophoneStatus] = useState('unknown');
    const [isTestingMicrophone, setIsTestingMicrophone] = useState(false);
    const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
    const [audioInputDevices, setAudioInputDevices] = useState([]);
    const [selectedInputDeviceId, setSelectedInputDeviceId] = useState('');

    const [voiceCallRegisterStatus, setVoiceCallRegisterStatus] = useState(voiceCallRegisterStatuses.idle);
    const [hasCallStarted, setHasCallStarted] = useState(false);

    const voiceCallId = useRef(null);
    const activeConnectionRef = useRef(null);
    const connectionListenersRef = useRef({});
    const reconnectTimeoutRef = useRef(null);
    const callStatusRef = useRef('');

    const { formatedTime: duration, start: startTimer, stop: stopTimer } = useTimer();

    const callIsInProgress = !!voiceCallStatuses[callStatus]?.isCallProgress;
    const isInCall = callStatus === voiceCallStatuses.inCall.key;
    const isCallFinished = callStatus === voiceCallStatuses.finished.key;
    const isMicrophoneReady = microphoneStatus === 'ok';
    const canStartCall = !!device && !tokenError && isMicrophoneReady && isOnline && !callIsInProgress && !hasCallStarted;

    useEffect(() => {
        callStatusRef.current = callStatus;
    }, [callStatus]);

    useEffect(() => {
        return () => {
            clearReconnectTimeout();
            cleanupConnectionListeners();
        };
    }, []);

    useEffect(() => {
        const onOnline = () => setIsOnline(true);
        const onOffline = () => setIsOnline(false);

        window.addEventListener('online', onOnline);
        window.addEventListener('offline', onOffline);

        return () => {
            window.removeEventListener('online', onOnline);
            window.removeEventListener('offline', onOffline);
        };
    }, []);

    useEffect(() => {
        generateToken().catch(error => onTokenError(error));
    }, []);

    useEffect(() => {

        if (!token)
            return;

        const newDevice = new Device(token, { debug: true, edge: selectedEdge || undefined });
        if (!newDevice)
            return;

        const onDeviceError = (error) => onError(error, voiceCallStatuses.error.key);
        const onTokenWillExpire = async () => {
            try {
                const refreshedToken = await generateToken(false);
                await newDevice.updateToken(refreshedToken);
            } catch (error) {
                onError(error, voiceCallStatuses.error.key);
            }
        };
        const onTokenExpired = () =>
            onError({ message: 'Sessão expirada. Reconecte para continuar.' }, voiceCallStatuses.error.key);
        const onIncoming = (connection) => connection.reject();

        newDevice.on('error', onDeviceError);
        newDevice.on('tokenWillExpire', onTokenWillExpire);
        newDevice.on('tokenExpired', onTokenExpired);
        newDevice.on('incoming', onIncoming);
        newDevice.register();
        setDevice(newDevice);

        return () => {
            newDevice.off('error', onDeviceError);
            newDevice.off('tokenWillExpire', onTokenWillExpire);
            newDevice.off('tokenExpired', onTokenExpired);
            newDevice.off('incoming', onIncoming);
            newDevice.disconnectAll();
            newDevice.destroy();
            setDevice(null);
        };
    }, [token, selectedEdge]);

    const generateToken = async (saveInState = true) => {
        setTokenError('');
        const data = await crmVoiceCallService.generateToken();
        if (saveInState) {
            setTokenState({
                token: data.token,
                info: { edgePolicy: data.edgePolicy },
                edge: selectBestEdge(data.edgePolicy)
            });
        }
        return data.token;
    };

    const onTokenError = (error) => {
        const message = error?.response?.data?.message || error?.message;
        setTokenError(message || 'Não foi possível preparar a ligação. Tente novamente.');
    };

    const retrySetup = () => generateToken().catch(error => onTokenError(error));

    const onError = (error, statusKey = voiceCallStatuses.error.key) => {
        const newErrorMessage = error?.response?.data?.message || error?.message;
        setErrorMessage(newErrorMessage || 'Ocorreu um erro na ligação.');
        setCallStatus(statusKey);
    };

    const clearReconnectTimeout = () => {
        if (!reconnectTimeoutRef.current)
            return;

        window.clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
    };

    const onReconnectTimeout = () => {
        clearReconnectTimeout();
        setCallEndReason('Tempo limite de reconexão excedido.');

        if (activeConnectionRef.current)
            activeConnectionRef.current.disconnect();
    };

    const cleanupConnectionListeners = () => {
        const connection = activeConnectionRef.current;
        if (!connection)
            return;

        Object.entries(connectionListenersRef.current).forEach(([eventName, callback]) => {
            connection.off(eventName, callback);
        });

        connectionListenersRef.current = {};
        activeConnectionRef.current = null;
    };

    const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    const refreshAudioInputDevices = useCallback(async () => {
        if (!navigator.mediaDevices?.enumerateDevices)
            return;

        const devices = await navigator.mediaDevices.enumerateDevices();
        const inputs = devices.filter(deviceInfo => deviceInfo.kind === 'audioinput');

        setAudioInputDevices(inputs);
        if (!selectedInputDeviceId && inputs[0]?.deviceId)
            setSelectedInputDeviceId(inputs[0].deviceId);
    }, [selectedInputDeviceId]);

    const checkVoiceCallId = async (currentCall) => {
        const newProviderCallId = currentCall?.parameters?.CallSid;

        if (!newProviderCallId) {
            setVoiceCallRegisterStatus(voiceCallRegisterStatuses.notFound);
            return null;
        }

        setVoiceCallRegisterStatus(voiceCallRegisterStatuses.searching);

        for (let attempt = 0; attempt < FIND_CALL_MAX_ATTEMPTS; attempt++) {
            const voiceCall = await crmVoiceCallService.findByProviderCallId(newProviderCallId).catch(() => ({}));

            if (voiceCall?.id) {
                voiceCallId.current = voiceCall.id;
                setVoiceCallRegisterStatus(voiceCallRegisterStatuses.found);
                return voiceCall.id;
            }

            await wait((attempt + 1) * 500);
        }

        setVoiceCallRegisterStatus(voiceCallRegisterStatuses.notFound);
        return null;
    };

    const getBrowserTelemetry = () => ({
        userAgent: navigator.userAgent || '',
        language: navigator.language || '',
        platform: navigator.platform || '',
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || ''
    });

    const getNetworkTelemetry = () => {
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        return {
            online: typeof navigator !== 'undefined' ? navigator.onLine : true,
            effectiveType: connection?.effectiveType || null,
            downlink: connection?.downlink || null,
            rtt: connection?.rtt || null,
            saveData: connection?.saveData || false
        };
    };

    const selectBestEdge = (edgePolicy = []) => {
        const policy = Array.isArray(edgePolicy) ? edgePolicy.filter(Boolean) : [];
        const primaryEdge = policy.find(edge => edge.includes('sao-paulo')) || policy[0] || 'sao-paulo';
        const fallbackEdge = policy.find(edge => edge !== primaryEdge) || primaryEdge;
        const networkInfo = getNetworkTelemetry();

        if ((networkInfo.rtt || 0) > HIGH_RTT_THRESHOLD_MS && fallbackEdge !== primaryEdge)
            return fallbackEdge;

        return primaryEdge;
    };

    const loadProviderCallStatus = useCallback(async (callSid) => {
        if (!callSid)
            return;

        const diagnosticsResponse = await crmVoiceCallService.findDiagnosticsByProviderCallId(callSid).catch(() => null);
        if (diagnosticsResponse)
            setProviderCallStatus(diagnosticsResponse?.technicalStatus?.status || diagnosticsResponse?.status || '');
    }, []);

    const sendTelemetry = async (callSid, eventName, additionalData = {}) => {
        if (!callSid)
            return;

        const networkInfo = getNetworkTelemetry();
        const edge = selectedEdge || (Array.isArray(tokenInfo?.edgePolicy) ? tokenInfo.edgePolicy[0] : null) || '-';

        return crmVoiceCallService
            .saveQualityTelemetryByProviderCallId(callSid, {
                event: eventName,
                edge,
                browser: getBrowserTelemetry(),
                network: networkInfo,
                warnings: additionalData.warnings || [],
                rtt: additionalData.rtt || networkInfo.rtt,
                jitter: additionalData.jitter || null,
                packetLoss: additionalData.packetLoss || null,
                edgePolicy: tokenInfo?.edgePolicy || []
            })
            .catch(() => null);
    };

    const onCallAccepted = async (currentCall) => {
        setErrorMessage('');
        setCallStatus(voiceCallStatuses.inCall.key);
        startTimer();
        const foundVoiceCallId = await checkVoiceCallId(currentCall);
        await sendTelemetry(currentCall?.parameters?.CallSid, 'accepted', {
            warnings: foundVoiceCallId ? [] : ['voiceCallId_not_found_yet']
        });
    };

    const onFinishVoiceCall = (reason = '') => {
        clearReconnectTimeout();
        cleanupConnectionListeners();
        if (reason)
            setCallEndReason(reason);
        setCallStatus(voiceCallStatuses.finished.key);
        setIsMuted(false);
        stopTimer();
    };

    const addCallEventListeners = (connection) => {
        cleanupConnectionListeners();
        activeConnectionRef.current = connection;

        const onRinging = (isConnected) =>
            setCallStatus(isConnected ? voiceCallStatuses.ringing.key : voiceCallStatuses.conecting.key);
        const onDisconnect = () => {
            const manualFinish = callStatusRef.current === voiceCallStatuses.finishing.key;
            const reason = manualFinish
                ? 'Ligação finalizada pelo operador.'
                : 'Chamada desligada pelo contato ou operadora.';
            sendTelemetry(connection.parameters?.CallSid, 'disconnect');
            onFinishVoiceCall(reason);
            loadProviderCallStatus(connection.parameters?.CallSid);
        };
        const onAccept = (currentCall) => onCallAccepted(currentCall);
        const onConnectionError = (error) => {
            setCallEndReason('Falha de conexão durante a chamada.');
            sendTelemetry(connection.parameters?.CallSid, 'connection_error', {
                warnings: [error?.message || 'connection_error']
            });
            loadProviderCallStatus(connection.parameters?.CallSid);
            onFinishVoiceCall('Falha de conexão durante a chamada.');
        };
        const onReconnecting = () => {
            setCallStatus(voiceCallStatuses.reconnecting.key);
            sendTelemetry(connection.parameters?.CallSid, 'reconnecting');
            clearReconnectTimeout();
            reconnectTimeoutRef.current = window.setTimeout(onReconnectTimeout, RECONNECT_TIMEOUT_MS);
        };
        const onReconnected = () => {
            clearReconnectTimeout();
            sendTelemetry(connection.parameters?.CallSid, 'reconnected');
            setCallStatus(voiceCallStatuses.inCall.key);
        };
        const onCancel = () => {
            sendTelemetry(connection.parameters?.CallSid, 'cancel');
            onFinishVoiceCall('Ligação cancelada antes de ser atendida.');
            loadProviderCallStatus(connection.parameters?.CallSid);
        };
        const onReject = () => {
            sendTelemetry(connection.parameters?.CallSid, 'reject');
            onFinishVoiceCall('Ligação rejeitada pelo contato.');
            loadProviderCallStatus(connection.parameters?.CallSid);
        };
        const onWarning = (warningName) => {
            sendTelemetry(connection.parameters?.CallSid, 'warning', { warnings: [warningName] });
        };
        const onWarningCleared = (warningName) => {
            sendTelemetry(connection.parameters?.CallSid, 'warning_cleared', { warnings: [warningName] });
        };
        const onMute = (newIsMuted) => setIsMuted(!!newIsMuted);

        connectionListenersRef.current = {
            ringing: onRinging,
            disconnect: onDisconnect,
            accept: onAccept,
            error: onConnectionError,
            reconnecting: onReconnecting,
            reconnected: onReconnected,
            cancel: onCancel,
            reject: onReject,
            warning: onWarning,
            'warning-cleared': onWarningCleared,
            mute: onMute
        };

        Object.entries(connectionListenersRef.current).forEach(([eventName, callback]) => {
            connection.on(eventName, callback);
        });
    };

    const checkMicrophoneAccess = async () => {
        if (!navigator.mediaDevices?.getUserMedia) {
            setMicrophoneStatus('blocked');
            return false;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            await refreshAudioInputDevices();
            stream.getTracks().forEach(track => track.stop());
            setMicrophoneStatus('ok');
            return true;
        } catch (error) {
            setMicrophoneStatus('blocked');
            return false;
        }
    };

    const testMicrophone = async () => {
        setIsTestingMicrophone(true);
        await checkMicrophoneAccess();
        setIsTestingMicrophone(false);
    };

    const changeInputDevice = async (newInputDeviceId) => {
        setSelectedInputDeviceId(newInputDeviceId);

        if (!newInputDeviceId || !device?.audio?.setInputDevice)
            return;

        await device.audio.setInputDevice(newInputDeviceId).catch(() => null);
    };

    useEffect(() => {
        if (!navigator.mediaDevices?.addEventListener)
            return;

        const onDeviceChange = () => refreshAudioInputDevices().catch(() => null);

        navigator.mediaDevices.addEventListener('devicechange', onDeviceChange);
        refreshAudioInputDevices().catch(() => null);

        return () => navigator.mediaDevices.removeEventListener('devicechange', onDeviceChange);
    }, [refreshAudioInputDevices]);

    useEffect(() => {
        if (!navigator.permissions?.query)
            return;

        let permissionStatus = null;

        const onPermissionChange = () => {
            const state = permissionStatus?.state || 'prompt';
            if (state === 'denied') {
                setMicrophoneStatus('blocked');

                if (voiceCallStatuses[callStatusRef.current]?.isCallProgress && activeConnectionRef.current) {
                    setCallEndReason('Permissão do microfone removida durante a chamada.');
                    activeConnectionRef.current.disconnect();
                }

                return;
            }

            if (state === 'granted')
                setMicrophoneStatus('ok');
        };

        navigator.permissions
            .query({ name: 'microphone' })
            .then((result) => {
                permissionStatus = result;
                permissionStatus.onchange = onPermissionChange;
                onPermissionChange();
            })
            .catch(() => null);

        return () => {
            if (permissionStatus)
                permissionStatus.onchange = null;
        };
    }, []);

    const startCall = async (phoneNumber) => {

        if (!phoneNumber) {
            onError({ message: 'Número de telefone inválido!' });
            return false;
        }

        if (!isOnline) {
            onError({ message: 'Sem conexão com a internet. Verifique a rede e tente novamente.' });
            return false;
        }

        if (!window.isSecureContext) {
            onError({ message: 'Ambiente inseguro para uso de microfone. Use HTTPS.' });
            return false;
        }

        const canCall = await checkMicrophoneAccess();
        if (!canCall) {
            onError({ message: 'Permita o uso do microfone para iniciar a chamada.' });
            return false;
        }

        setErrorMessage('');
        setProviderCallStatus('');
        setCallEndReason('');
        setCallStatus(voiceCallStatuses.conecting.key);
        setHasCallStarted(true);

        try {
            const outgoingConnection = await crmVoiceCallService.startCall(device, phoneNumber, opportunityId, contactId, customerId);
            addCallEventListeners(outgoingConnection);
            return true;
        } catch (error) {
            setHasCallStarted(false);
            setCallStatus('');
            onError({ message: error?.message || 'Não foi possível iniciar a chamada. Tente novamente.' }, '');
            return false;
        }
    };

    const hangup = () => {
        if (!device)
            return;

        setCallStatus(voiceCallStatuses.finishing.key);
        clearReconnectTimeout();
        device.disconnectAll();
    };

    const toggleMute = () => {
        const connection = activeConnectionRef.current;
        if (!connection?.mute)
            return;

        const newIsMuted = !connection.isMuted?.();
        connection.mute(newIsMuted);
        setIsMuted(newIsMuted);
    };

    const saveVoiceCallResult = async ({ outcome, callerComment, activityId = null }) => {
        if (!voiceCallId.current)
            throw { message: 'Não foi possível localizar o registro da chamada.' };

        return crmVoiceCallService.update(voiceCallId.current, {
            outcome,
            callerComment,
            ...(activityId && { activityId }),
        });
    };

    const getProviderEndReason = (status) => {
        const reasonByStatus = {
            completed: 'Ligação finalizada com sucesso.',
            'no-answer': 'Chamada não atendida.',
            busy: 'Linha ocupada.',
            failed: 'Chamada encerrada por falha de conexão.',
            canceled: 'Chamada cancelada antes do atendimento.'
        };

        return reasonByStatus[(status || '').toLowerCase()] || '';
    };

    return {
        callStatus,
        callIsInProgress,
        isInCall,
        isCallFinished,
        hasCallStarted,
        errorMessage,
        callEndReason: getProviderEndReason(providerCallStatus) || callEndReason,
        tokenError,
        retrySetup,
        isDeviceReady: !!device,
        canStartCall,
        startCall,
        hangup,
        isMuted,
        toggleMute,
        duration,
        isOnline,
        microphoneStatus,
        isMicrophoneReady,
        isTestingMicrophone,
        testMicrophone,
        audioInputDevices,
        selectedInputDeviceId,
        changeInputDevice,
        voiceCallId: voiceCallId.current,
        voiceCallRegisterStatus,
        saveVoiceCallResult
    };
};

export default useVoiceCallEngine;
